import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints } from '@rossum/api-client';
import { QueueAction, SchemaSection } from '@rossum/api-client/schemas';
import {
  GridCellParams,
  GridColDef,
  GridEditInputCell,
  GridRenderCellParams,
  GridRenderEditCellParams,
} from '@rossum/ui/x-data-grid-pro';
import { api } from '../../../../lib/apiClient';
import { FORMULA_PREVIEW_KEY } from '../../keys';
import { ActionCell } from '../cells/ActionCell';
import { AutocompleteEditCell } from '../cells/AutocompleteEditCell';
import { ConditionCell } from '../cells/ConditionCell';
import {
  DatapointCellRenderer,
  formulaPreviewClassName,
} from '../cells/DatapointCell';
import { DocumentCell } from '../cells/DocumentCell';
import { HeaderCell } from '../cells/HeaderCell';
import { isUnusedDependency } from '../cells/utils';
import { flattenDatapoints } from '../flattenDatapoints';
import {
  actionsColumn,
  columnsPrefix,
  conditionsColumn,
  DatapointColumn,
  documentsColumn,
  globals,
  RowModel,
} from '../rows/helpers';
import { useColumnWidths } from './useColumnWidths';
import { useLookupDependenciesByQuery } from './useLookupDependenciesByQuery';
import { resolveIconType } from './utils';

export const COLUMNS_QUERY_KEY = [
  FORMULA_PREVIEW_KEY,
  'formulas-info',
] as const;

type UseColumnsParams = {
  currentFormulaId: string;
  schemaForPreview: SchemaSection[];
  type: 'rule' | 'formula';
  ActionComponent?: React.FC<{ action: QueueAction }> | undefined;
  queueName: string | undefined;
};

export const useColumns = ({
  currentFormulaId,
  schemaForPreview,
  type,
  ActionComponent,
  queueName,
}: UseColumnsParams): GridColDef<RowModel>[] => {
  const intl = useIntl();

  const formulasInfoQuery = useQuery({
    queryKey: [...COLUMNS_QUERY_KEY, schemaForPreview],
    queryFn: () =>
      api.request(
        endpoints.internal.schemas.formulasInfo({
          // using getter to use the current schema concept when invalidated query with unsaved changes
          schemaContent: schemaForPreview,
        })
      ),
    enabled: schemaForPreview.length > 0,
    cacheTime: 0,
  });

  const columnWidths = useColumnWidths();

  const currentFormulaInfo = formulasInfoQuery.data?.results.find(
    d => d.id === currentFormulaId
  );

  const usedContextFields = currentFormulaInfo?.context;

  const formulaType = currentFormulaInfo?.type ?? 'formula';

  const flatSchema = useMemo(
    () => flattenDatapoints(schemaForPreview),
    [schemaForPreview]
  );

  const schemaMap = useMemo(() => {
    return Object.fromEntries(
      flatSchema.map(
        schemaItem =>
          [
            schemaItem.id,
            {
              label: schemaItem.label,
              icon: resolveIconType(schemaItem),
              options:
                schemaItem.type === 'enum' ? schemaItem.options : undefined,
              category: schemaItem.category,
              type: schemaItem.type,
            },
          ] as const
      )
    );
  }, [flatSchema]);

  const dependenciesByQuery = useLookupDependenciesByQuery(
    flatSchema,
    currentFormulaId,
    formulaType
  );

  const currentFormulaColumn: DatapointColumn = `${columnsPrefix}.${currentFormulaId}`;

  const formulaDependenciesColumns = useMemo(() => {
    const dependencies = currentFormulaInfo?.dependencies ?? [];

    return dependencies.flatMap(dependencySchemaId => {
      const field = `${columnsPrefix}.${dependencySchemaId}` as const;
      const dependencySchema = schemaMap[dependencySchemaId];
      const hasOptions = !!dependencySchema?.options?.length;

      const shouldFilterDependency =
        dependencySchemaId === currentFormulaId ||
        dependencySchema?.category !== 'datapoint' ||
        dependencySchema?.type === 'button';

      return shouldFilterDependency
        ? []
        : ({
            field,
            headerName: dependencySchemaId,
            width: columnWidths[field] ?? 200,
            minWidth: 120,
            sortable: false,
            editable: true,
            renderCell: (params: GridRenderCellParams) => (
              <DatapointCellRenderer
                params={params}
                schema={schemaMap[dependencySchemaId]}
                isUnusedDependency={isUnusedDependency(
                  params.row,
                  dependencySchemaId,
                  dependenciesByQuery,
                  currentFormulaColumn
                )}
              />
            ),
            type: 'string',
            renderEditCell: (params: GridRenderEditCellParams) => {
              if (hasOptions) {
                return (
                  <AutocompleteEditCell
                    {...params}
                    options={dependencySchema.options}
                  />
                );
              }
              return (
                <GridEditInputCell
                  {...params}
                  size="small"
                  value={params.value?.preview ?? params.value ?? ''}
                />
              );
            },
            valueSetter: (value, row) => {
              if (typeof value === 'string') {
                return {
                  ...row,
                  [field]: {
                    ...row[field],
                    preview: value,
                  },
                };
              }
              if (value && typeof value === 'object' && 'preview' in value) {
                return {
                  ...row,
                  [field]: {
                    ...row[field],
                    preview: (value as { preview: string }).preview,
                  },
                };
              }
              return row;
            },

            renderHeader: () => (
              <HeaderCell
                primaryText={dependencySchema?.label}
                secondaryText={dependencySchemaId}
                iconType={dependencySchema?.icon ?? null}
              />
            ),
          } satisfies GridColDef<RowModel, string | undefined>);
    });
  }, [
    currentFormulaInfo?.dependencies,
    currentFormulaId,
    columnWidths,
    schemaMap,
    dependenciesByQuery,
    currentFormulaColumn,
  ]);

  return [
    ...(type === 'rule'
      ? [
          {
            field: conditionsColumn,
            headerName: currentFormulaId,
            width: 80,
            sortable: false,
            resizable: false,
            renderCell: ({ row }: GridCellParams<RowModel>) => (
              <ConditionCell value={row[conditionsColumn]} />
            ),
            cellClassName: formulaPreviewClassName,
            renderHeader: () => (
              <HeaderCell
                primaryText={intl.formatMessage({
                  id: 'features.formulas.when',
                })}
                secondaryText={intl.formatMessage({
                  id: 'features.formulas.condition',
                })}
              />
            ),
          },
          {
            field: actionsColumn,
            headerName: currentFormulaId,
            sortable: false,
            minWidth: 70,
            resizable: true,
            width: columnWidths[actionsColumn] ?? 100,
            renderCell: (params: GridCellParams<RowModel>) => {
              return (
                <ActionCell
                  actions={
                    params.row[conditionsColumn]
                      ? (params.row[actionsColumn] ?? [])
                      : []
                  }
                  ActionComponent={ActionComponent}
                  width={params.colDef.width}
                />
              );
            },
            cellClassName: formulaPreviewClassName,
            renderHeader: () => (
              <HeaderCell
                primaryText={intl.formatMessage({
                  id: 'features.formulas.then',
                })}
                secondaryText={intl.formatMessage({
                  id: 'features.formulas.actions',
                })}
              />
            ),
          },
        ]
      : [
          {
            field: currentFormulaColumn,
            headerName: currentFormulaId,
            width: columnWidths[currentFormulaColumn] ?? 200,
            minWidth: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
              <DatapointCellRenderer
                params={params}
                schema={schemaMap[currentFormulaId]}
                isCurrentSchema
              />
            ),
            cellClassName: formulaPreviewClassName,
            renderHeader: () => (
              <HeaderCell
                primaryText={schemaMap[currentFormulaId]?.label}
                secondaryText={currentFormulaId}
                iconType={formulaType}
                usedContextFields={usedContextFields}
              />
            ),
          },
        ]),
    ...formulaDependenciesColumns,
    {
      field: documentsColumn,
      headerName: intl.formatMessage({
        id: 'features.queueSettings.fields.formulaPreview.table.document',
      }),
      width: columnWidths[documentsColumn] ?? 200,
      minWidth: 200,
      sortable: false,
      renderHeader: p => (
        <HeaderCell
          primaryText={p.colDef.headerName}
          secondaryText={queueName ?? ''}
        />
      ),
      renderCell: p => (
        <DocumentCell
          annotationUrl={p.row.annotationUrl}
          value={p.value}
          // passing globals to document column due to the inability to have dynamic width of columns
          // based on number of icons, could be tackled by `autosizeOptions` in DataGrid v7 probably
          globals={p.row[globals]}
        />
      ),
    },
  ];
};
